(ns ui.view.card.shared-components.entity-table
  (:require
   [cljs.reader :as edn]
   [clojure.string :as string]
   [common.localization :as loc]
   [common.util :as cu]
   [oops.core :as oops]
   [reagent.core :as r]
   [ui.components.grid.view :refer [ag-grid-enterprise theme-quartz]]))

(defn value-getter
  [field js-params]
  (let [params (js->clj js-params :keywordize-keys true)
        value-map (-> params
                      :data
                      (get field))]
    value-map))

(defn value-formatter
  [field js-params]
  (let [params (js->clj js-params :keywordize-keys true)

        value-map-or-edn-string (:value params)
        value-map (if (string? value-map-or-edn-string)
                    (edn/read-string value-map-or-edn-string)
                    value-map-or-edn-string)
        {:keys [value materialized-value]} value-map
        group? (some-> params
                       :node
                       (oops/oget "group"))]
    (cond
      (and group? (= field :task/completed?))
      (if value
        (loc/tr :completed)
        (loc/tr :to-do))

      (and group? (cu/blank-value? materialized-value))
      (loc/tr :table-row-group-no-value [(loc/tr field)])

      (coll? materialized-value)
      (->> materialized-value
           (mapv :label)
           (sort)
           (string/join "; "))

      :else
      (str materialized-value))))

;; TODO: maybe separate parseInt and parseFloat.
(defn column-value-comparator
  [value-map-a value-map-b _node-a _node-b _descending?]
  (let [numeric? (:numeric? value-map-a)
        value-a (cond numeric?
                      (some-> value-map-a
                              :value
                              js/parseFloat)
                      (coll? (:materialized-value value-map-a))
                      (string/join "; " (:materialized-value value-map-a))
                      :else
                      (-> value-map-a
                          :materialized-value
                          str))
        value-b (cond numeric?
                      (some-> value-map-b
                              :value
                              js/parseFloat)
                      (coll? (:materialized-value value-map-b))
                      (string/join "; " (:materialized-value value-map-b))
                      :else
                      (-> value-map-b
                          :materialized-value
                          str))

        result (compare value-a value-b)]

    result))

(defn first-column-cell-renderer
  [open-button-text js-params]
  (let [params (js->clj js-params :keywordize-keys true)
        value-formatted (:valueFormatted params)]
    (r/as-element
     [:div
      {:class "relative flex items-center w-full h-full overflow-hidden"}
      [:div
       {:class "grow truncate"}
       value-formatted]
      [:div
       {:class "hidden group-hover/row:block"}
       [:button
        {:class "shadow rounded border bg-base-light hover:text-base-light-text text-xs m-0"}
        (or open-button-text
            (loc/tr :preview))]]])))

(defn create-column-defs
  [{:keys [columns sort-model group-by-field preview-button?]
    :or   {preview-button? true}
    :as   _table-config}]
  (let [sort-index-and-direction-by-field
        (->> sort-model
             (map-indexed
              (fn [idx {:keys [field direction]}]
                [field {:sort-index idx
                        :direction  direction}]))
             (into {}))
        data-columns (->> columns
                          (map-indexed
                           (fn [col-ord {:keys [field open-button-text label]}]
                             (let [last-column? (= col-ord (dec (count columns)))
                                   sort? (contains? sort-index-and-direction-by-field field)
                                   group? (= field group-by-field)]
                               (merge
                                {:headerName                 (or label (loc/tr field))
                                 :flex                       (when last-column? 1)
                                 :field                      (cu/kw->str field)
                                 :valueGetter                (partial value-getter field)
                                 :valueFormatter             (partial value-formatter field)
                                 :comparator                 column-value-comparator
                                 :useValueFormatterForExport true}
                                (when (and (zero? col-ord)
                                           preview-button?)
                                  {:cellRenderer (partial first-column-cell-renderer open-button-text)})
                                (when group?
                                  {:rowGroup true})
                                (when sort?
                                  {:sortIndex (get-in sort-index-and-direction-by-field [field :sort-index])
                                   :sort      (get-in sort-index-and-direction-by-field [field :direction])})))))
                          (vec))
        all-columns data-columns]
    all-columns))

(defn data->ag-row-data
  [rows]
  (->> rows
       (mapv (fn [row-data]
               (->> row-data
                    (mapv (fn [[k v]]
                            [(cu/kw->str k) v]))
                    (into {}))))))

(defn view
  [{:keys [data config on-row-clicked] :as _props}]
  (let [col-defs (create-column-defs config)
        ag-row-data (data->ag-row-data data)]
    [:<>
     [:div
      {:style {:height "auto"}}
      [:div
       {:class "h-full w-full"}
       [ag-grid-enterprise
        (merge
         {:theme                      theme-quartz
          :columnDefs                 col-defs
          :rowData                    ag-row-data
          :rowClass                   "group/row hover:bg-base-regular"
          :suppressContextMenu        true
          :defaultColDef              {:sortable                 true
                                       :suppressHeaderMenuButton true
                                       :suppressColumnsToolPanel true
                                       :suppressFiltersToolPanel true
                                       :autoSize                 true
                                       :minWidth                 150
                                       :resizable                true
                                       :suppressMovable          true
                                       :menuTabs                 []
                                       :cellClass                "overflow-hidden"
                                       ;; HACK: For some reason cell text truncation does not work with inline tailwind css here.
                                       ;; Issue likely in some .scss file
                                       :cellRenderer             (fn [params]
                                                                   (r/as-element
                                                                    [:div
                                                                     {:class "truncate"}
                                                                     (oops/oget params "valueFormatted")]))}
          :useValueFormatterForExport true
          :processCellForClipboard    (fn [params]
                                        (or (some-> params
                                                    (oops/oget "value")
                                                    (js->clj :keywordize-keys true)
                                                    :materialized-value)
                                            ""))
          :getRowId                   (fn [params]
                                        (-> params
                                            (oops/oget "data")
                                            (oops/oget "db/id")
                                            (oops/oget "value")))
          :onRowClicked               (fn [params]
                                        (when (fn? on-row-clicked)
                                          (when-let [js-data (oops/oget params "data")]
                                            (let [entity-id (-> js-data
                                                                (oops/oget "db/id")
                                                                (oops/oget "value"))]
                                              (on-row-clicked {:entity-id entity-id})))))
          :suppressRowHoverHighlight  true
          ;;:suppressHorizontalScroll true
          :groupDefaultExpanded       0
          :suppressMultiSort          true
          :groupDisplayType           :groupRows
          :domLayout                  :autoHeight
          :suppressFiltersToolPanel   true})]]]]))
